import { createTheme, alpha } from '@mui/material/styles';

// M3-orientiertes Schema. MUI v6's experimental_extendTheme erlaubt
// Light/Dark via CSS-Variablen (colorSchemes). Wir mappen die wichtigsten
// M3-Color-Roles auf MUI-Tokens und bilden M3-spezifische Surface-/Container-
// Tokens als zusätzliche CSS-Variablen ab.
//
// Source-Color: Schüco-Stahlblau (#3B5266) → Tonal Palettes generieren
// in einer späteren Iteration über den Material Theme Builder. Für jetzt
// sind die Werte handgemischt nach M3-Tonal-Spec (siehe m3.material.io).

// M3-Tonal-Paletten als Hex (verkürzte Skala: 10/20/30/40/50/60/70/80/90/95)
const PRIMARY = {
  10: '#001E2C', 20: '#003547', 30: '#1B4D63', 40: '#3B6679', 50: '#557F93',
  60: '#6F9AAE', 70: '#88B5C9', 80: '#A4D0E6', 90: '#C7E7FF', 95: '#E5F2FF',
};
const SECONDARY = {
  10: '#0F1D24', 20: '#253239', 30: '#3B484F', 40: '#536067', 50: '#6C7980',
  60: '#85929A', 70: '#9FADB4', 80: '#BAC8CF', 90: '#D6E4EB', 95: '#E5F2FA',
};
const NEUTRAL = {
  10: '#191C1E', 20: '#2E3133', 30: '#45474A', 40: '#5C5F61', 50: '#75787A',
  60: '#8F9194', 70: '#A9ACAE', 80: '#C5C7CA', 90: '#E1E3E6', 95: '#F0F1F4', 99: '#FBFCFE',
};
const NEUTRAL_VARIANT = {
  10: '#161D21', 20: '#2B3236', 30: '#42494D', 40: '#596065', 50: '#71787E',
  60: '#8B9297', 70: '#A6ACB2', 80: '#C1C7CD', 90: '#DDE3E9', 95: '#EBF1F7',
};
const ERROR = {
  10: '#410002', 20: '#690005', 30: '#93000A', 40: '#BA1A1A', 50: '#DE3730',
  60: '#FF5449', 70: '#FF897D', 80: '#FFB4AB', 90: '#FFDAD6', 95: '#FFEDEA',
};

// M3 → MUI Mapping
export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-theme' },
  colorSchemes: {
    light: {
      palette: {
        primary:   { main: PRIMARY[40],   contrastText: '#FFFFFF', light: PRIMARY[80], dark: PRIMARY[30] },
        secondary: { main: SECONDARY[40], contrastText: '#FFFFFF', light: SECONDARY[80], dark: SECONDARY[30] },
        error:     { main: ERROR[40],     contrastText: '#FFFFFF' },
        background:{ default: NEUTRAL[99], paper: NEUTRAL[99] },
        text:      { primary: NEUTRAL[10], secondary: NEUTRAL_VARIANT[30] },
        divider:   NEUTRAL_VARIANT[80],
      },
    },
    dark: {
      palette: {
        primary:   { main: PRIMARY[80],   contrastText: PRIMARY[20], light: PRIMARY[90], dark: PRIMARY[40] },
        secondary: { main: SECONDARY[80], contrastText: SECONDARY[20] },
        error:     { main: ERROR[80],     contrastText: ERROR[20] },
        background:{ default: NEUTRAL[10], paper: NEUTRAL[20] },
        text:      { primary: NEUTRAL[90], secondary: NEUTRAL_VARIANT[80] },
        divider:   NEUTRAL_VARIANT[30],
      },
    },
  },
  shape: {
    borderRadius: 12, // M3 medium rounded corner
  },
  typography: {
    fontFamily: '"Roboto Flex", "Roboto", "Helvetica", "Arial", sans-serif',
    // M3 Type Scale (verkürzt – Display/Headline/Title/Body/Label)
    h1: { fontSize: '57px', lineHeight: '64px', fontWeight: 400, letterSpacing: '-0.25px' },
    h2: { fontSize: '45px', lineHeight: '52px', fontWeight: 400 },
    h3: { fontSize: '36px', lineHeight: '44px', fontWeight: 400 },
    h4: { fontSize: '32px', lineHeight: '40px', fontWeight: 400 },
    h5: { fontSize: '28px', lineHeight: '36px', fontWeight: 400 },
    h6: { fontSize: '22px', lineHeight: '28px', fontWeight: 500 },
    subtitle1: { fontSize: '16px', lineHeight: '24px', fontWeight: 500, letterSpacing: '0.15px' }, // Title Medium
    subtitle2: { fontSize: '14px', lineHeight: '20px', fontWeight: 500, letterSpacing: '0.1px' },  // Title Small
    body1:     { fontSize: '16px', lineHeight: '24px', fontWeight: 400, letterSpacing: '0.5px' },  // Body Large
    body2:     { fontSize: '14px', lineHeight: '20px', fontWeight: 400, letterSpacing: '0.25px' }, // Body Medium
    button:    { fontSize: '14px', lineHeight: '20px', fontWeight: 500, letterSpacing: '0.1px', textTransform: 'none' }, // M3: no UPPERCASE
    caption:   { fontSize: '12px', lineHeight: '16px', fontWeight: 400, letterSpacing: '0.4px' },
    overline:  { fontSize: '11px', lineHeight: '16px', fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase' },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 20, // M3 full-rounded buttons
          paddingInline: 24,
          minHeight: 40,
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.92) },
        }),
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'medium', fullWidth: true },
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { borderRadius: 4 } }, // M3 text-fields haben kleine Radien
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0, variant: 'outlined' },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'saturate(180%) blur(10px)',
        }),
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
    },
  },
});
